import { useEffect, useRef, useState } from "react";
import { DockedPanel, dockedPanelScrollRegionProps } from "../layout/DockedPanel";
import { Comment, type CommentsProps } from "./Comments";

export type CommentsTabContentProps = Pick<CommentsProps, "comments" | "composer"> & {
	/** Whether the panel hosting this content is currently open. Drives the scroll-to-latest. */
	commentsOpen: boolean;
};

/**
 * The message list + composer, without the `DockedPanel` chrome. Extracted so a host that already
 * provides its own docked panel (e.g. one that toggles between comments and another tool) can
 * embed the same comments experience without nesting a second panel/toggle inside its own.
 */
export const CommentsTabContent = ({
	comments,
	composer,
	commentsOpen,
}: CommentsTabContentProps) => {
	const messageContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!commentsOpen) {
			if (!!comments && comments.length && messageContainerRef.current) {
				messageContainerRef.current.scroll({
					top: messageContainerRef.current.scrollHeight,
					behavior: "auto",
				});
			}
		}
	}, [comments, commentsOpen]);

	return (
		<>
			<div
				ref={messageContainerRef}
				className="flex flex-col space-y-[20px] flex-1 w-full pt-[12px] pb-[24px] pr-[4px] bg-surface-neutral/60 border-y border-y-bc-default overflow-y-auto overscroll-y-contain"
				data-testid="comments-tab-messages"
				{...dockedPanelScrollRegionProps}
			>
				{!!comments && !comments.length && (
					<div className="self-center text-[12px] font-medium text-subtle mt-[12px]">
						No comments
					</div>
				)}
				{!!comments && comments.map((comment, index) => <Comment key={index} comment={comment} />)}
			</div>
			<div className="[&_div]:focus-within:outline-none">{composer}</div>
		</>
	);
};

export const CommentsTab = ({
	comments,
	composer,
	label,
	contained,
	trackingId,
}: CommentsProps & { label: string; contained?: boolean; trackingId?: string }) => {
	const [commentsOpen, setCommentsOpen] = useState(false);

	return (
		<DockedPanel
			contained={contained}
			label={label}
			onOpenChange={setCommentsOpen}
			testId="comments-tab"
			trackingId={trackingId}
		>
			<CommentsTabContent comments={comments} commentsOpen={commentsOpen} composer={composer} />
		</DockedPanel>
	);
};
